import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import api from '../../services/api';
import { FREQ_OPTIONS, FREQ_DEFAULTS, FrequencyConfig } from './CareCatalogPanel';

const CURRENCIES = ['USD','EUR','GBP','CAD','AUD','NZD','ZAR','CHF','JPY','HKD'];

/**
 * Bulk-edit selected scheduled care items that share the same category + type.
 * Only fields the user changes are applied — blank/default fields are ignored.
 */
export default function BulkEditScheduledModal({ open, onClose, onSaved, items, locations, catalogItems }) {
  const [preferred_time, setPreferredTime]   = useState('');
  const [start_date, setStartDate]           = useState('');
  const [location_id, setLocationId]         = useState('');
  const [notes, setNotes]                    = useState('');
  const [new_catalog_id, setNewCatalogId]    = useState('');

  // Frequency change (opt-in)
  const [changeFreq, setChangeFreq]          = useState(false);
  const [freqType, setFreqType]              = useState('daily');
  const [freqConfig, setFreqConfig]          = useState({ ...FREQ_DEFAULTS.daily });

  // Billable change (opt-in)
  const [changeBillable, setChangeBillable]  = useState(false);
  const [billable, setBillable]              = useState(false);
  const [cost, setCost]                      = useState('');
  const [cost_currency, setCostCurrency]     = useState('USD');

  const [saving, setSaving]                  = useState(false);

  const sample = items[0] || {};

  const sameCatTypeItems = (catalogItems || []).filter(
    c => c.category === sample.category && c.type === sample.type
  );

  const handleSave = async () => {
    const hasChange = preferred_time || start_date || (location_id && location_id !== '__none__') ||
      notes.trim() || (new_catalog_id && new_catalog_id !== '__none__') || changeFreq || changeBillable;
    if (!hasChange) { toast.error('Change at least one field'); return; }

    setSaving(true);
    try {
      const update = {};

      if (preferred_time) update.preferred_time = preferred_time;
      if (start_date)     update.start_date = start_date;
      if (location_id && location_id !== '__none__') {
        update.location_id   = location_id;
        update.location_name = locations?.find(l => l.id === location_id)?.name || '';
      }
      if (notes.trim()) update.notes = notes.trim();

      if (new_catalog_id && new_catalog_id !== '__none__') {
        const cat = (catalogItems || []).find(c => c.id === new_catalog_id);
        if (cat) {
          update.care_catalog_item_id = cat.id;
          update.name        = cat.name;
          update.dosage      = cat.dosage || '';
          update.dosage_unit = cat.dosage_unit || '';
          // Carry billable info from the new catalog item (unless user has overridden below)
          if (!changeBillable && cat.billable) {
            update.billable      = true;
            update.cost          = cat.cost;
            update.cost_currency = cat.cost_currency || 'USD';
          }
        }
      }

      if (changeFreq) {
        update.frequency_type   = freqType;
        update.frequency_config = freqConfig;
      }

      if (changeBillable) {
        update.billable = billable;
        if (billable && cost !== '') {
          update.cost          = parseFloat(cost) || null;
          update.cost_currency = cost_currency;
        } else if (!billable) {
          update.cost = null;
        }
      }

      await Promise.allSettled(
        items.map(item => api.put(`/farmfolio/scheduled-care/${item.id}`, { ...item, ...update }))
      );
      toast.success(`Updated ${items.length} item(s)`);
      onSaved();
      onClose();
    } catch { toast.error('Failed to update'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Edit — {items.length} item(s)</DialogTitle>
        </DialogHeader>

        <div className="text-xs text-muted-foreground border rounded px-2 py-2 bg-muted/20">
          <span className="font-medium text-foreground">{sample.category} / {sample.type} — {sample.name}</span>
        </div>
        <p className="text-xs text-muted-foreground">Leave fields blank / toggles off to keep existing values.</p>

        <div className="space-y-3">
          {/* Change Item */}
          {sameCatTypeItems.length > 1 && (
            <div className="space-y-1">
              <Label className="text-xs font-semibold">Change Item</Label>
              <Select value={new_catalog_id || '__none__'} onValueChange={v => setNewCatalogId(v === '__none__' ? '' : v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="No change" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— No change —</SelectItem>
                  {sameCatTypeItems.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}{c.dosage
                        ? ` (${c.dosage} ${c.dosage_unit || ''})`.trim()
                        : c.serving_size != null
                          ? ` (${c.serving_size}${c.serving_size_units ? ' ' + c.serving_size_units : ''})`
                          : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Preferred Time */}
          <div className="space-y-1">
            <Label className="text-xs font-semibold">Preferred Time</Label>
            <Input type="time" value={preferred_time} onChange={e => setPreferredTime(e.target.value)}
              className={preferred_time ? 'time-filled' : ''} />
          </div>

          {/* Start Date */}
          <div className="space-y-1">
            <Label className="text-xs font-semibold">Start Date</Label>
            <Input type="date" value={start_date} onChange={e => setStartDate(e.target.value)}
              className={start_date ? 'time-filled' : ''} />
          </div>

          {/* Location */}
          <div className="space-y-1">
            <Label className="text-xs font-semibold">Location</Label>
            <Select value={location_id || '__none__'} onValueChange={v => setLocationId(v === '__none__' ? '' : v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="No change" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— No change —</SelectItem>
                {(locations || []).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-1">
            <Label className="text-xs font-semibold">Notes</Label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Leave blank to keep existing" />
          </div>

          {/* Frequency (opt-in) */}
          <div className="space-y-2 border rounded p-2 bg-muted/10">
            <div className="flex items-center gap-2">
              <Switch checked={changeFreq} onCheckedChange={v => setChangeFreq(v)} />
              <Label className="text-xs font-semibold cursor-pointer">Change Frequency</Label>
            </div>
            {changeFreq && (
              <div className="space-y-2 pt-1">
                <Select value={freqType} onValueChange={v => {
                  setFreqType(v);
                  setFreqConfig({ ...FREQ_DEFAULTS[v] });
                }}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FREQ_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="border rounded p-2 bg-white">
                  <FrequencyConfig
                    form={{ frequency_type: freqType, frequency_config: freqConfig }}
                    setForm={patch => {
                      const updated = typeof patch === 'function'
                        ? patch({ frequency_type: freqType, frequency_config: freqConfig })
                        : patch;
                      if (updated.frequency_type) setFreqType(updated.frequency_type);
                      if (updated.frequency_config) setFreqConfig(updated.frequency_config);
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Billable (opt-in) */}
          <div className="space-y-2 border rounded p-2 bg-muted/10">
            <div className="flex items-center gap-2">
              <Switch checked={changeBillable} onCheckedChange={v => setChangeBillable(v)} />
              <Label className="text-xs font-semibold cursor-pointer">Change Billable</Label>
            </div>
            {changeBillable && (
              <div className="space-y-2 pt-1">
                <div className="flex items-center gap-2">
                  <Switch checked={billable} onCheckedChange={setBillable} />
                  <Label className="text-xs cursor-pointer">Billable</Label>
                </div>
                {billable && (
                  <div className="flex gap-2">
                    <Select value={cost_currency} onValueChange={setCostCurrency}>
                      <SelectTrigger className="w-20 h-7 text-xs shrink-0"><SelectValue /></SelectTrigger>
                      <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" min="0" step="0.01" value={cost}
                      onChange={e => setCost(e.target.value)}
                      placeholder="Cost" className="h-7 text-xs flex-1" />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}
            className="bg-primary hover:bg-primary/90 text-white">
            {saving ? 'Saving…' : `Update ${items.length} item(s)`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
